import {
    getDefaultColorOption,
    getBaseColorOptionsByProvider,
    applyColorOrderWithDividers,
} from '@/utils/colorOptions';
import { DateValueType } from 'react-tailwindcss-datepicker';
import { getDateRangeForPeriod, DateRangeOptions } from '@/utils/time';
import { WeekStart } from '@/i18n/weekStart';

// 検索パラメータのインターフェース
export interface SearchParams {
    startDate?: string;
    endDate?: string;
    keyword?: string;
    // 色選択（暫定：フロント側のみ保持）
    color?: ColorOption;
}

// カレンダー選択用インターフェース
export interface CalendarOption {
    value: string;
    label: string;
}

// 色選択用（現状はカレンダーの型と同一）
export type ColorOption = CalendarOption & {
    colorHex?: string;
    isDivider?: boolean;
    originalLabel?: string;
};

// カレンダーリストのインターフェース
export interface CalendarItem {
    providerId: string;
    providerAccountName: string;
    providerName?: string; // 'google' | 'azure' など
}

/**
 * 日付をYYYY-MM-DD形式に変換する（タイムゾーンの問題を回避）
 * @param date 変換する日付
 * @returns YYYY-MM-DD形式の文字列
 */
export const formatDateForServer = (date: Date | string): string => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * 初期日付を設定する
 * @param initialParams 初期パラメータ
 * @returns DateValueType形式の日付
 */
interface WeekStartOptions {
    weekStartsOn?: WeekStart;
}

export const getInitialDate = (
    initialParams?: SearchParams,
    options: WeekStartOptions = {},
): DateValueType => {
    const dateRangeOptions: DateRangeOptions = {
        weekStartsOn: options.weekStartsOn,
    };
    if (initialParams?.startDate && initialParams?.endDate) {
        return {
            startDate: new Date(initialParams.startDate),
            endDate: new Date(initialParams.endDate),
        };
    }
    // 共通ロジックで先週の範囲を取得
    const { start, end } = getDateRangeForPeriod('LAST_WEEK', dateRangeOptions);
    return {
        startDate: start,
        endDate: end,
    };
};

/**
 * カレンダーリストからオプション配列を作成
 * @param calendarList カレンダーリスト
 * @returns Select用のオプション配列
 */
export const createCalendarOptions = (
    calendarList?: CalendarItem[],
    fallbackLabel: string = 'アカウント名なし',
): CalendarOption[] => {
    if (!calendarList || calendarList.length === 0) {
        return [];
    }

    return calendarList.map((calendar) => ({
        value: calendar.providerId,
        label: calendar.providerAccountName || fallbackLabel,
    }));
};

/**
 * カレンダーリストからデフォルト値を取得
 * @param calendarList カレンダーリスト
 * @returns デフォルトのカレンダーオプション
 */
export const getDefaultCalendarOption = (
    calendarList?: CalendarItem[],
    fallbackLabel: string = 'アカウント名なし',
): CalendarOption => {
    if (!calendarList || calendarList.length === 0) {
        return { value: '', label: '' };
    }

    return {
        value: calendarList[0].providerId,
        label: calendarList[0].providerAccountName || fallbackLabel,
    };
};

/**
 * Datepickerのショートカット設定を取得
 * @returns ショートカット設定
 */
export interface ShortcutLabels {
    today: string;
    yesterday: string;
    past7Days: string;
    past14Days?: string;
    past30Days: string;
    past3Months?: string;
    past6Months?: string;
    past12Months?: string;
    pastWeek: string;
    currentWeek: string;
    pastMonth: string;
    currentMonth: string;
}

export const getDatepickerShortcuts = (
    isPaid: boolean = false,
    labels?: ShortcutLabels,
    options: WeekStartOptions = {},
) => {
    const dateRangeOptions: DateRangeOptions = {
        weekStartsOn: options.weekStartsOn,
    };
    const l = {
        today: labels?.today ?? 'Today',
        yesterday: labels?.yesterday ?? 'Yesterday',
        past7Days: labels?.past7Days ?? 'Past 7 days',
        past14Days: labels?.past14Days ?? 'Past 14 days',
        past30Days: labels?.past30Days ?? 'Past 30 days',
        past3Months: labels?.past3Months ?? 'Past 3 months',
        past6Months: labels?.past6Months ?? 'Past 6 months',
        past12Months: labels?.past12Months ?? 'Past 12 months',
        pastWeek: labels?.pastWeek ?? 'Last week',
        currentWeek: labels?.currentWeek ?? 'This week',
        pastMonth: labels?.pastMonth ?? 'Last month',
        currentMonth: labels?.currentMonth ?? 'This month',
    };

    return {
        today: l.today,
        yesterday: l.yesterday,
        past7Days: {
            text: l.past7Days,
            period: (() => {
                const { start, end } = getDateRangeForPeriod('PAST_7_DAYS', dateRangeOptions);
                return { start, end };
            })(),
        },
        past14Days: {
            text: l.past14Days,
            period: (() => {
                const { start, end } = getDateRangeForPeriod('PAST_14_DAYS', dateRangeOptions);
                return { start, end };
            })(),
        },
        past30Days: {
            text: l.past30Days,
            period: (() => {
                const { start, end } = getDateRangeForPeriod('PAST_30_DAYS', dateRangeOptions);
                return { start, end };
            })(),
        },
        /* 有料プランの場合のみ表示（紹介特典含む） */
        ...(isPaid
            ? {
                  past3Months: {
                      text: l.past3Months,
                      period: (() => {
                          const { start, end } = getDateRangeForPeriod(
                              'PAST_3_MONTHS',
                              dateRangeOptions,
                          );
                          return { start, end };
                      })(),
                  },
                  past6Months: {
                      text: l.past6Months,
                      period: (() => {
                          const { start, end } = getDateRangeForPeriod(
                              'PAST_6_MONTHS',
                              dateRangeOptions,
                          );
                          return { start, end };
                      })(),
                  },
                  past12Months: {
                      text: l.past12Months,
                      period: (() => {
                          const { start, end } = getDateRangeForPeriod(
                              'PAST_12_MONTHS',
                              dateRangeOptions,
                          );
                          return { start, end };
                      })(),
                  },
              }
            : {}),
        pastWeek: {
            text: l.pastWeek,
            period: (() => {
                const { start, end } = getDateRangeForPeriod('LAST_WEEK', dateRangeOptions);
                return { start, end };
            })(),
        },
        currentWeek: {
            text: l.currentWeek,
            period: (() => {
                const { start, end } = getDateRangeForPeriod('THIS_WEEK', dateRangeOptions);
                return { start, end };
            })(),
        },
        pastMonth: l.pastMonth,
        currentMonth: l.currentMonth,
    };
};

/**
 * 色選択のデフォルト値（指定なし）
 */
export { getDefaultColorOption };

/**
 * プロバイダー名から色オプションの基本リストを取得
 */
export { getBaseColorOptionsByProvider };

/**
 * 並び替え: 指定なし → 仕切り → すべて → 仕切り → 使用順(MRU) → 仕切り → その他
 */
export { applyColorOrderWithDividers };
